import {
    computed,
    defineComponent,
    onMounted,
    ref,
} from "vue";
import type { LogRole } from "../../core/types.js";
import {
    DiscordPermissionFlags,
    type DiscordPermissionFlag,
} from "../../logs/common/role/permissions.js";
import {
    listGuildRoles,
    updateRolePermissions,
} from "./api.js";
import RolePickerDialog from "../../shared/roles/RolePickerDialog.js";
import PermissionChangeSummary from "./PermissionChangeSummary.js";
import { RoleChip } from "./RoleChip.js";
import {
    Steps,
    combineReferencePermissions,
    diffPermissions,
    formatPermissionLabel,
    getRoleBits,
    sortRoles,
    type DraftMode,
    type PendingConfirmation,
    type RolePickerMode,
    type StepId,
} from "./rolePermissionsModel.js";

export default defineComponent({
    name: "BulkRolePermissions",

    setup() {
        const roles = ref<LogRole[]>([]);
        const targetRoleIds = ref<string[]>([]);
        const referenceRoleIds = ref<string[]>([]);
        const activeStep = ref<StepId>("targets");
        const draftMode = ref<DraftMode>("first-target");
        const draftPermissions = ref<bigint | null>(null);
        const rolePicker = ref<RolePickerMode | null>(null);
        const loading = ref(true);
        const saving = ref(false);
        const error = ref<string | null>(null);
        const resultMessage = ref<string | null>(null);
        const confirmation = ref<PendingConfirmation | null>(null);

        const sortedRoles = computed(() => sortRoles(roles.value));
        const selectedTargetRoles = computed(() => resolveRoles(targetRoleIds.value));
        const selectedReferenceRoles = computed(() => resolveRoles(referenceRoleIds.value));
        const activeStepMeta = computed(() => Steps.find((step) => step.id === activeStep.value)!);

        const effectiveDraft = computed(() => {
            if (draftPermissions.value !== null)
                return draftPermissions.value;

            if (draftMode.value === "empty")
                return 0n;

            if (draftMode.value === "references")
                return combineReferencePermissions(roles.value, referenceRoleIds.value);

            const firstRole = selectedTargetRoles.value[0];
            return firstRole ? getRoleBits(firstRole) : 0n;
        });

        const changes = computed(() => selectedTargetRoles.value
            .map((role) => ({ role, before: getRoleBits(role), after: effectiveDraft.value }))
            .filter((entry) => entry.before !== entry.after)
            .map((entry) => ({ ...entry, changedPermissions: diffPermissions(entry.before, entry.after) })));

        function resolveRoles(roleIds: string[]): LogRole[] {
            return roleIds
                .map((roleId) => roles.value.find((role) => role.id === roleId))
                .filter((role): role is LogRole => Boolean(role));
        }

        function canOpenStep(step: StepId): boolean {
            return step === "targets" || targetRoleIds.value.length > 0;
        }

        function setActiveStep(step: StepId): void {
            if (canOpenStep(step))
                activeStep.value = step;
        }

        function openRolePicker(mode: RolePickerMode): void {
            rolePicker.value = mode;
        }

        function closeRolePicker(): void {
            rolePicker.value = null;
        }

        function updatePickedRoles(roleIds: string[]): void {
            if (rolePicker.value === "references") {
                referenceRoleIds.value = roleIds;
                if (draftMode.value === "references")
                    draftPermissions.value = combineReferencePermissions(roles.value, referenceRoleIds.value);
                return;
            }

            const nextTargetRoleIds = new Set(roleIds);
            targetRoleIds.value = roleIds;
            referenceRoleIds.value = referenceRoleIds.value.filter((roleId) => !nextTargetRoleIds.has(roleId));
            resultMessage.value = null;

            if (targetRoleIds.value.length === 0) {
                activeStep.value = "targets";
                draftPermissions.value = null;
            }
        }

        function removeTargetRole(roleId: string): void {
            targetRoleIds.value = targetRoleIds.value.filter((id) => id !== roleId);
            if (targetRoleIds.value.length === 0)
                activeStep.value = "targets";
        }

        function removeReferenceRole(roleId: string): void {
            referenceRoleIds.value = referenceRoleIds.value.filter((id) => id !== roleId);

            if (draftMode.value === "references")
                draftPermissions.value = combineReferencePermissions(roles.value, referenceRoleIds.value);
        }

        function setDraftMode(mode: DraftMode): void {
            draftMode.value = mode;
            const firstTarget = selectedTargetRoles.value[0];
            draftPermissions.value = mode === "empty" ? 0n
                : mode === "references" ? combineReferencePermissions(roles.value, referenceRoleIds.value)
                    : firstTarget ? getRoleBits(firstTarget) : 0n;
        }

        function togglePermission(permission: DiscordPermissionFlag): void {
            const current = effectiveDraft.value;
            draftPermissions.value = (current & permission.bit) !== 0n
                ? current & ~permission.bit
                : current | permission.bit;
            resultMessage.value = null;
        }

        function countTargetsWith(permission: DiscordPermissionFlag): number {
            return selectedTargetRoles.value.filter((role) => (getRoleBits(role) & permission.bit) !== 0n).length;
        }

        function openConfirmation(): void {
            if (targetRoleIds.value.length === 0 || changes.value.length === 0)
                return;

            confirmation.value = {
                roleIds: [ ...targetRoleIds.value ],
                permissions: effectiveDraft.value.toString(),
                changes: [ ...changes.value ],
            };
        }

        async function confirmSave(): Promise<void> {
            if (!confirmation.value || saving.value)
                return;

            saving.value = true;
            error.value = null;

            try {
                const response = await updateRolePermissions(confirmation.value);
                const roleMap = new Map(roles.value.map((role) => [ role.id, role ]));

                for (const change of response.changes) {
                    const role = roleMap.get(change.roleId);
                    if (role)
                        role.permissions = change.after;
                }

                resultMessage.value = `${response.changes.length} rôle(s) mis à jour.`;
                if (response.skipped.length > 0)
                    resultMessage.value += ` ${response.skipped.length} rôle(s) ignoré(s).`;

                confirmation.value = null;

            } catch (err) {
                error.value = err instanceof Error ? err.message : "Impossible de modifier les rôles.";

            } finally {
                saving.value = false;
            }
        }

        onMounted(async () => {
            try {
                roles.value = await listGuildRoles();
            } catch (err) {
                error.value = err instanceof Error ? err.message : "Impossible de charger les rôles.";
            } finally {
                loading.value = false;
            }
        });

        const renderStepContent = () => {
            if (activeStep.value === "targets")
                return (
                    <div class="bulk-role-step-card">
                        <div class="bulk-role-step-copy">
                            <strong>Rôles qui seront modifiés</strong>
                            <span>Sélectionne uniquement les rôles de sortie. Les rôles de référence ne seront jamais modifiés.</span>
                        </div>

                        <button class="bulk-role-big-action" onClick={() => openRolePicker("targets")}>
                            <i class="material-symbols-outlined">add_circle</i>
                            Sélectionner les rôles
                        </button>

                        <div class="bulk-role-selected-list">
                            {selectedTargetRoles.value.length === 0
                                ? <em>Aucun rôle cible sélectionné.</em>
                                : selectedTargetRoles.value.map((role) => (
                                    <RoleChip key={role.id} role={role} selected removable onClick={() => removeTargetRole(role.id)} />
                                ))}
                        </div>

                        <button class="bulk-role-next" disabled={targetRoleIds.value.length === 0} onClick={() => setActiveStep("model")}>
                            Continuer
                        </button>
                    </div>
                );

            if (activeStep.value === "model")
                return (
                    <div class="bulk-role-step-card">
                        <div class="bulk-role-model-grid">
                            <button class={{ "bulk-role-model": true, active: draftMode.value === "first-target" }} onClick={() => setDraftMode("first-target")}>
                                <i class="material-symbols-outlined">looks_one</i>
                                <strong>Premier rôle cible</strong>
                                <span>Utilise les permissions actuelles du premier rôle sélectionné.</span>
                            </button>

                            <button class={{ "bulk-role-model": true, active: draftMode.value === "references" }} onClick={() => setDraftMode("references")}>
                                <i class="material-symbols-outlined">content_copy</i>
                                <strong>Rôles de référence</strong>
                                <span>Fusionne les permissions des rôles choisis comme modèle.</span>
                            </button>

                            <button class={{ "bulk-role-model": true, active: draftMode.value === "empty" }} onClick={() => setDraftMode("empty")}>
                                <i class="material-symbols-outlined">backspace</i>
                                <strong>Partir de zéro</strong>
                                <span>Démarre avec aucune permission, puis ajuste manuellement.</span>
                            </button>
                        </div>

                        <div class="bulk-role-reference-row">
                            <button onClick={() => openRolePicker("references")}>
                                <i class="material-symbols-outlined">badge</i>
                                Sélectionner les références
                            </button>
                            <span>{referenceRoleIds.value.length} rôle(s) de référence</span>
                        </div>

                        <div class="bulk-role-selected-list">
                            {selectedReferenceRoles.value.length === 0
                                ? <em>Aucun rôle de référence.</em>
                                : selectedReferenceRoles.value.map((role) => (
                                    <RoleChip key={role.id} role={role} selected removable onClick={() => removeReferenceRole(role.id)} />
                                ))}
                        </div>

                        <button class="bulk-role-next" onClick={() => setActiveStep("permissions")}>
                            Éditer les permissions
                        </button>
                    </div>
                );

            return (
                <div class="bulk-role-step-card permissions">
                    <div class="bulk-role-permission-toolbar">
                        <div>
                            <strong>Permissions finales</strong>
                            <span>{changes.value.length} rôle(s) auront des changements.</span>
                        </div>
                        <button onClick={() => setActiveStep("model")}>Changer le modèle</button>
                    </div>

                    <div class="bulk-role-permissions-grid">
                        {DiscordPermissionFlags.map((permission) => {
                            const enabled = (effectiveDraft.value & permission.bit) !== 0n;
                            const targetCount = countTargetsWith(permission);

                            return (
                                <button
                                    key={permission.key}
                                    class={{ "bulk-role-permission": true, enabled, privileged: "privileged" in permission && permission.privileged }}
                                    onClick={() => togglePermission(permission)}
                                >
                                    <i class="material-symbols-outlined">{enabled ? "toggle_on" : "toggle_off"}</i>
                                    <span>
                                        <strong>{formatPermissionLabel(permission)}</strong>
                                        <small>{permission.key}</small>
                                    </span>
                                    <em>{targetCount}/{selectedTargetRoles.value.length}</em>
                                </button>
                            );
                        })}
                    </div>

                    <div class="bulk-role-savebar">
                        <div>
                            <strong>{changes.value.length} rôle(s) modifié(s)</strong>
                            <span>{changes.value.length === 0 ? "Aucun changement à appliquer." : "Récapitulatif obligatoire avant application."}</span>
                        </div>
                        <button disabled={changes.value.length === 0 || saving.value} onClick={openConfirmation}>
                            Prévisualiser
                        </button>
                    </div>
                </div>
            );
        };

        return () => (
            <div class="bulk-role-page">
                <div class="bulk-role-hero">
                    <div>
                        <span class="bulk-role-eyebrow">Outils · Permissions Discord</span>
                        <h1>Éditer les permissions de rôles</h1>
                        <p>Un flow contrôlé pour appliquer des permissions serveur à plusieurs rôles sans modification directe non confirmée.</p>
                    </div>

                    <div class="bulk-role-summary">
                        <strong>{targetRoleIds.value.length}</strong><span>cibles</span>
                        <strong>{referenceRoleIds.value.length}</strong><span>références</span>
                        <strong>{changes.value.length}</strong><span>changements</span>
                    </div>
                </div>

                <div class="bulk-role-mode-switch">
                    <button class="active"><i class="material-symbols-outlined">badge</i> Éditer rôle</button>
                    <button disabled><i class="material-symbols-outlined">tag</i> Éditer salons</button>
                </div>

                {error.value && <div class="bulk-role-alert error">{error.value}</div>}
                {resultMessage.value && <div class="bulk-role-alert">{resultMessage.value}</div>}

                <div class="bulk-role-stepper">
                    {Steps.map((step, index) => (
                        <button
                            key={step.id}
                            class={{ active: activeStep.value === step.id, disabled: !canOpenStep(step.id) }}
                            disabled={!canOpenStep(step.id)}
                            onClick={() => setActiveStep(step.id)}
                        >
                            <span>{index + 1}</span>
                            <strong>{step.label}</strong>
                        </button>
                    ))}
                </div>

                <div class="bulk-role-active-title">
                    <span>Étape active</span>
                    <h2>{activeStepMeta.value.title}</h2>
                </div>

                {loading.value ? <div class="bulk-role-skeleton">Chargement des rôles…</div> : renderStepContent()}

                {rolePicker.value && (
                    <RolePickerDialog
                        roles={sortedRoles.value}
                        selectedRoleIds={rolePicker.value === "references" ? referenceRoleIds.value : targetRoleIds.value}
                        disabledRoleIds={rolePicker.value === "references" ? targetRoleIds.value : []}
                        title={rolePicker.value === "references" ? "Sélectionner les rôles de référence" : "Sélectionner les rôles cibles"}
                        subtitle={rolePicker.value === "references"
                            ? "Ces rôles servent uniquement de modèle. Ils ne seront pas modifiés."
                            : "Ces rôles recevront les permissions finales après confirmation."}
                        confirmLabel="Valider la sélection"
                        onChange={updatePickedRoles}
                        onClose={closeRolePicker}
                    />
                )}

                {confirmation.value && (
                    <div class="bulk-role-modal-backdrop">
                        <div class="bulk-role-modal">
                            <div class="bulk-role-modal-header">
                                <div>
                                    <strong>Confirmer les modifications</strong>
                                    <span>{confirmation.value.changes.length} rôle(s) seront modifié(s).</span>
                                </div>
                                <button onClick={() => confirmation.value = null}><i class="material-symbols-outlined">close</i></button>
                            </div>

                            <PermissionChangeSummary changes={confirmation.value.changes} />

                            <div class="bulk-role-modal-actions">
                                <button onClick={() => confirmation.value = null}>Annuler</button>
                                <button class="primary" disabled={saving.value} onClick={confirmSave}>
                                    {saving.value ? "Application…" : "Confirmer"}
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        );
    },
});
